/**
 * 移动端顶部栏：左侧可返回箭头 + 标题；右侧搜索入口（PRD §4.4）。
 *
 * 设计规则：
 * - 首页（/home）显示「序章」标题，不显示返回箭头；
 * - 其他任何路由均显示返回箭头（回到历史记录中的上一级）+ 路径对应的中文标题；
 * - PRD §4.4：仅在首页 /home 显示「搜索」icon + 「搜索」文字。
 */

import React from 'react';
import TopIcon from './TopIcon';

export interface MobileHeaderProps {
  /** 当前页中文标题 */
  title?: string;
  /** 是否显示左侧返回箭头（默认 false） */
  showBack?: boolean;
  /** 返回箭头点击回调 */
  onBack?: () => void;
  /** 返回按钮右侧的首页快捷入口。 */
  onHome?: () => void;
  onSearch?: () => void;
  /**
   * 搜索按钮样式：
   *   - 'icon'        ：只显示圆形 icon 按钮（次级页默认）；
   *   - 'icon-text'   ：显示图标 + 「搜索」文字（首页 PRD §4.4 要求）。
   */
  searchMode?: 'icon' | 'icon-text';
  /** 搜索按钮左侧的辅助入口（例如通知）。 */
  beforeSearch?: React.ReactNode;
  trailing?: React.ReactNode;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  title = '序章',
  showBack = false,
  onBack,
  onHome,
  onSearch,
  searchMode = 'icon',
  beforeSearch,
  trailing,
}) => {
  return (
    <header className="fp-mobile-header">
      <div className="fp-mobile-header__left">
        {showBack ? (
          <>
            <button
              type="button"
              className="fp-mobile-header__back"
              onClick={onBack}
              aria-label="返回上一级"
              data-testid="header-back"
            >
              <TopIcon name="back" size={22} className="fp-mobile-header__raster-icon" />
            </button>
            {onHome ? (
              <button
                type="button"
                className="fp-mobile-icon-btn"
                onClick={onHome}
                aria-label="回到首页"
                data-testid="header-home"
              >
                <TopIcon name="home" size={20} className="fp-mobile-header__raster-icon" />
              </button>
            ) : null}
          </>
        ) : null}
        <div className={`fp-mobile-header__title${showBack ? ' fp-mobile-header__title--with-controls' : ''}`}>
          {title}
        </div>
      </div>
      <div className="fp-mobile-header__actions">
        {beforeSearch}
        {onSearch ? (
          searchMode === 'icon-text' ? (
            <button
              className="fp-mobile-header__search-text"
              onClick={onSearch}
              aria-label="搜索"
              data-testid="header-search"
              type="button"
            >
              <TopIcon name="search" size={18} className="fp-mobile-header__raster-icon" />
              <span>搜索</span>
            </button>
          ) : (
            <button
              className="fp-mobile-icon-btn"
              aria-label="搜索"
              data-testid="header-search"
              onClick={onSearch}
              type="button"
            >
              <TopIcon name="search" size={20} className="fp-mobile-header__raster-icon" />
            </button>
          )
        ) : null}
        {trailing}
      </div>
    </header>
  );
};

export default MobileHeader;
